import {
  Box,
  Button,
  Divider,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ReactComponent as AssignmentIcon } from "../../../assets/assignment.svg";
import { ReactComponent as ForwardInboxIcon } from "../../../assets/forward_inbox.svg";
import QuillEditor from "../../../components/QuilEditor/QuilEditor";
import { quillHtmlToEmailBody } from "../../../components/QuilEditor/quillHtmlToEmailBody";
import SelectedItemsPreview from "../../../components/SelectedItemsPreview/SelectedItemsPreview";
import { usePageMetadata } from "../../../context/PageMetadataContext";
import { usePortal } from "../../../context/PortalsContext";
import { useToast } from "../../../context/ToastContext";
import { useAuth } from "../../../context/AuthContext";
import {
  getEmailTemplates,
  getEmailTemplate,
  sendEmail,
} from "../../../services/api/apiClient";
import {
  EmailTemplateShortResult,
  SendToQueueBodyParams,
  SendEmailUser,
  EmailTemplateTypeEnum,
} from "../../../services/api/types/emailTemplates";
import { brand, gray } from "../../../theme/themePrimitives";
import { ModeSelectorCard } from "./components/ModeSelectorCard";
import { RecipientsPreviewModal } from "./components/RecipientsPreviewModal";

const EmailSend: React.FC = () => {
  const location = useLocation();
  const [mode, setMode] = useState<"custom" | "template">("template");
  const [template, setTemplate] = useState("");
  const [templateType, setTemplateType] =
    useState<EmailTemplateTypeEnum | null>(null);
  const [templates, setTemplates] = useState<EmailTemplateShortResult[]>([]);
  const [recipientsModalOpen, setRecipientsModalOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const { showToast } = useToast();
  const { portalUid } = useParams<{ portalUid: string }>();
  const { auth } = useAuth();
  const navigate = useNavigate();

  // Get recipients from route state and transform to match component formats
  const selectedUsers = (location.state?.selectedUsers as any[]) || [];
  const recipients = selectedUsers.map((user) => {
    const fullName =
      `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
      user.name ||
      "";
    return {
      id: user.UserPortalUid || user.id,
      name: fullName,
      email: user.email,
      title: fullName,
      subtitle: user.email,
    };
  });

  const { portals } = usePortal();
  useEffect(() => {
    const fetchTemplates = async () => {
      if (!portals.selectedPortal?.portalUid) return;
      try {
        const response = (await getEmailTemplates(
          portals.selectedPortal.portalUid,
          { short: true },
        )) as EmailTemplateShortResult[];
        setTemplates(Array.isArray(response) ? response : []);
      } catch (err) {
        console.error("Failed to load email templates", err);
      }
    };
    fetchTemplates();
  }, [portals.selectedPortal]);

  // Load template details when a template is selected
  useEffect(() => {
    const loadTemplateDetails = async () => {
      if (!template || !portals.selectedPortal?.portalUid) {
        return;
      }
      try {
        const details = await getEmailTemplate(
          portals.selectedPortal.portalUid,
          template,
        );
        if (details) {
          setFromEmail((prev) => details.sender || prev);
          setSubject((prev) => details.subject || prev);
          setMessage((prev) => details.message || prev);
          setTemplateType(details.type ?? null);
        }
      } catch (err) {
        console.error("Failed to load template details", err);
      }
    };
    loadTemplateDetails();
  }, [template, portals.selectedPortal]);
  const [fromEmail, setFromEmail] = useState("elearning@igacademy.com");
  const [replyTo, setReplyTo] = useState("elearning@igacademy.com");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const handleSend = () => {
    if (!portals.selectedPortal?.portalUid) {
      showToast("No portal selected", "error");
      return;
    }

    if (recipients.length === 0) {
      showToast("Please select at least one recipient", "error");
      return;
    }

    if (!auth.user?.UserPortalUid) {
      showToast("User not authenticated", "error");
      return;
    }

    if (mode === "template" && !template) {
      showToast("Please select a template", "error");
      return;
    }

    if (mode === "custom" && (!subject || !message)) {
      showToast("Please fill in subject and message", "error");
      return;
    }

    const portalId = portals.selectedPortal.portalUid;

    const userBody: SendEmailUser[] = selectedUsers.map((u) => ({
      userPortalUid: u.UserPortalUid || u.id,
      firstname: u.firstName || "",
      lastname: u.lastName || "",
      email: u.email,
    }));

    const body: SendToQueueBodyParams = {
      users: userBody,
      adminUser: auth.user?.UserPortalUid || "",
    };

    if (mode === "template") {
      body.emailTemplateUid = template;
    } else {
      body.subject = subject;
      body.messageHtml = quillHtmlToEmailBody(message);
      body.sender = fromEmail;
      body.replyTo = replyTo || undefined;
    }

    setSending(true);
    sendEmail(portalId, body)
      .then((res) => {
        if (res === "OK") {
          showToast("Email request sent to queue", "success");
        } else {
          const baseMessage =
            templateType === EmailTemplateTypeEnum.NEW_ASSIGNMENT
              ? "New assignment emails were not sent for the following users due to ineligible status or no new assignment"
              : templateType === EmailTemplateTypeEnum.INVITE_USER
                ? "Invitation emails were not sent for the following users due to ineligible status"
                : "Emails were not sent for the following users due to ineligible status";
          showToast(`${baseMessage}: ${res}`, "warning", null);
        }
        navigate(-1);
      })
      .catch((err) => {
        console.error(err);
        showToast("Failed to send email", "error");
      })
      .finally(() => setSending(false));
  };

  usePageMetadata(
    {
      title: "Send email",
      breadcrumbs: [
        {
          label: "User management",
          path: `/portal-configuration/${portalUid}/users`,
        },
        {
          label: "User search",
          path: `/portal-configuration/${portalUid}/users`,
        },
        {
          label: "Send email",
        },
      ],
    },
    [portalUid],
  );

  return (
    <Box>
      {/* Recipients Section */}
      <SelectedItemsPreview
        items={recipients}
        itemType="emails"
        onViewDetails={() => setRecipientsModalOpen(true)}
      />

      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          {/* Mode selector (Custom vs Template) */}
          <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
            <ModeSelectorCard
              title="Use template"
              description="Select from saved templates with pre-configured content and placeholders"
              icon={<AssignmentIcon />}
              selected={mode === "template"}
              onClick={() => setMode("template")}
              primaryButtonColor={brand[600]}
            />
            <ModeSelectorCard
              title="Custom email"
              description="Compose a one-time email with custom content for specific recipients"
              icon={<ForwardInboxIcon />}
              selected={mode === "custom"}
              onClick={() => {
                setMode("custom");
                setTemplate("");
                setSubject("");
                setMessage("");
              }}
              primaryButtonColor={brand[600]}
            />
          </Box>
          <Divider />

          {/* Email configuration card-like area */}
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            Template configuration
          </Typography>

          <Divider />

          {/* Show template selector only when template mode is active */}
          {mode === "template" && (
            <>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 0.75, fontSize: "0.875rem" }}
                >
                  Select template
                  <Box component="span" sx={{ color: "error.main" }}>
                    *
                  </Box>
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={template}
                    displayEmpty
                    onChange={(e) => {
                      const val = e.target.value as string;
                      setTemplate(val);
                      // Template details will be loaded via useEffect
                    }}
                  >
                    <MenuItem value="" disabled>
                      Choose a template
                    </MenuItem>
                    {templates.map((t) => (
                      <MenuItem
                        key={t.emailTemplateId}
                        value={t.emailTemplateId}
                      >
                        {t.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Divider sx={{ my: 2 }} />
            </>
          )}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{ mb: 0.75, fontSize: "0.875rem" }}
              >
                From
                <Box component="span" sx={{ color: "error.main" }}>
                  *
                </Box>
              </Typography>

              <FormControl fullWidth size="small">
                <Select
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                  displayEmpty
                  sx={{ height: 36 }}
                >
                  <MenuItem value="elearning@igacademy.com">
                    elearning@igacademy.com
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{ mb: 0.75, fontSize: "0.875rem" }}
              >
                Reply to
                <Box component="span" sx={{ color: "error.main" }}>
                  *
                </Box>
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
              />
            </Box>
          </Stack>

          <Box>
            <Typography variant="body2" sx={{ mb: 0.75, fontSize: "0.875rem" }}>
              Subject
              <Box component="span" sx={{ color: "error.main" }}>
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={mode === "template"}
              sx={{
                backgroundColor: mode === "template" ? gray[50] : undefined,
              }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box>
            <Typography variant="body2" sx={{ mb: 0.75, fontSize: "0.875rem" }}>
              Message
              <Box component="span" sx={{ color: "error.main" }}>
                *
              </Box>
            </Typography>
            <QuillEditor
              value={message}
              onChange={setMessage}
              disabled={mode === "template"}
              mode={mode}
            />
          </Box>

          <Box sx={{ textAlign: "center", mt: 1 }}>
            <Button
              sx={{
                px: 2,
                borderRadius: "12px",
                backgroundColor: `${brand[600]} !important`,
                color: "white",
                "&:hover": {
                  bgcolor: `${brand[700]} !important`,
                },
              }}
              onClick={handleSend}
              disabled={sending}
            >
              Send
            </Button>
          </Box>
        </Stack>
      </Paper>

      <RecipientsPreviewModal
        open={recipientsModalOpen}
        onClose={() => setRecipientsModalOpen(false)}
        recipients={recipients}
      />
    </Box>
  );
};

export default EmailSend;
